import { cloneElement, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { portfolioOptions, systemOptions } from '../data/content';
import { buildWhatsAppUrl, site } from '../data/site';
import { planOptions } from '../data/pricing';
import {
  createProtocol,
  formatPhone,
  leadWhatsAppUrl,
  saveLead,
} from '../lib/leads';
import { getAttribution, trackEvent } from '../lib/tracking';

const emptyForm = {
  name: '',
  office: '',
  whatsapp: '',
  portfolio: '',
  system: '',
  companyWebsite: '',
};

export function DiagnosticForm({ compact = false, selectedPlan = '', onPlanChange = () => {} }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = field => event => {
    const value = field === 'whatsapp' ? formatPhone(event.target.value) : event.target.value;
    setForm(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = 'Informe seu nome.';
    if (form.office.trim().length < 2) next.office = 'Informe o escritório.';
    if (form.whatsapp.replace(/\D/g, '').length < 10) next.whatsapp = 'Informe um WhatsApp válido.';
    if (!form.portfolio) next.portfolio = 'Selecione o tamanho da carteira.';
    if (!form.system) next.system = 'Selecione o sistema atual.';
    return next;
  };

  const handleSubmit = event => {
    event.preventDefault();
    if (form.companyWebsite) return;

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);

    const lead = saveLead({
      protocol: createProtocol(),
      name: form.name.trim(),
      office: form.office.trim(),
      whatsapp: form.whatsapp.trim(),
      portfolio: form.portfolio,
      system: form.system,
      plan: selectedPlan || 'Quero ajuda para escolher',
      attribution: getAttribution(),
      createdAt: new Date().toISOString(),
    });

    trackEvent('Contact', {
      content_name: 'mensagem-contdesk-preparada',
      content_category: 'demonstracao',
    });

    window.open(leadWhatsAppUrl(lead), '_blank', 'noopener,noreferrer');
    window.history.pushState({}, '', '/obrigado');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <form className={`lead-form ${compact ? 'lead-form--compact' : ''}`} onSubmit={handleSubmit} noValidate>
      <div className="lead-form-grid">
        <Field label="Plano de interesse" wide>
          <select id="lead-plan" name="plan" value={selectedPlan} onChange={event => onPlanChange(event.target.value)}>
            <option value="">Quero ajuda para escolher</option>
            {planOptions.filter(option => option !== 'Quero ajuda para escolher').map(option => <option value={option} key={option}>{option}</option>)}
          </select>
        </Field>
        <Field label="Seu nome" error={errors.name}>
          <input
            id="lead-name"
            name="name"
            autoComplete="name"
            value={form.name}
            onChange={update('name')}
            aria-invalid={Boolean(errors.name)}
          />
        </Field>

        <Field label="Escritório" error={errors.office}>
          <input
            id="lead-office"
            name="office"
            autoComplete="organization"
            value={form.office}
            onChange={update('office')}
            aria-invalid={Boolean(errors.office)}
          />
        </Field>

        <Field label="WhatsApp" error={errors.whatsapp}>
          <input
            id="lead-whatsapp"
            name="whatsapp"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="(11) 99999-9999"
            value={form.whatsapp}
            onChange={update('whatsapp')}
            aria-invalid={Boolean(errors.whatsapp)}
          />
        </Field>

        <Field label="Quantas empresas clientes?" error={errors.portfolio}>
          <select
            id="lead-portfolio"
            name="portfolio"
            value={form.portfolio}
            onChange={update('portfolio')}
            aria-invalid={Boolean(errors.portfolio)}
          >
            <option value="">Selecione</option>
            {portfolioOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </Field>

        <Field label="Qual sistema usam hoje?" error={errors.system} wide>
          <select
            id="lead-system"
            name="system"
            value={form.system}
            onChange={update('system')}
            aria-invalid={Boolean(errors.system)}
          >
            <option value="">Selecione</option>
            {systemOptions.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </Field>
      </div>

      <div className="visually-hidden" aria-hidden="true">
        <label htmlFor="lead-website">Site</label>
        <input
          id="lead-website"
          name="company_website"
          tabIndex={-1}
          autoComplete="off"
          value={form.companyWebsite}
          onChange={update('companyWebsite')}
        />
      </div>

      <button className="button button--cyan lead-form-submit" type="submit" disabled={submitting}>
        {submitting ? 'Preparando mensagem…' : 'Continuar no WhatsApp'}
        <ArrowRight />
      </button>

      <p className="lead-form-note">
        O site prepara a mensagem. Confirme o envio no WhatsApp para a RMS receber seu pedido.
        Usamos seus dados para atender esta solicitação, conforme a <a href={site.privacyUrl}>Política de Privacidade</a>. Sem compromisso de contratação.
        Se preferir, <a href={buildWhatsAppUrl()} target="_blank" rel="noreferrer">converse direto no WhatsApp</a>.
      </p>
    </form>
  );
}

function Field({ label, error, wide = false, children }) {
  const controlId = children.props.id;

  return (
    <label className={`lead-field ${wide ? 'lead-field--wide' : ''}`} htmlFor={controlId}>
      <span>{label}</span>
      {cloneElement(children, { 'aria-label': label, 'aria-describedby': error ? `${controlId}-error` : undefined })}
      {error ? <small id={`${controlId}-error`} role="alert">{error}</small> : null}
    </label>
  );
}
